import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useApprovalRequest, useApprove, useReject } from '../hooks/useApprovalCenter';
import { ApprovalFullTimeline } from '../components/ApprovalTimeline';
import { ApprovalDecisionDialog } from '../components/ApprovalDecisionDialog';
import type { ApprovalStatus, ApiError } from '../types/approvalCenter';
import type { StatusTone } from '@ui-kit';

function statusTone(status: ApprovalStatus): StatusTone {
  if (status === 'APPROVED') return 'success';
  if (status === 'REJECTED') return 'danger';
  if (status === 'CANCELLED') return 'warning';
  return 'info';
}

function formatDateTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

function TimelineSkeletonRow() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-3)', paddingBottom: 'var(--spacing-4)' }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'var(--color-surface-elevated)',
          flexShrink: 0,
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <div style={{ flex: 1, paddingTop: 'var(--spacing-1)' }}>
        <div style={{ height: 12, width: '40%', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-elevated)', animation: 'pulse 1.5s ease-in-out infinite', marginBottom: 'var(--spacing-2)' }} />
        <div style={{ height: 10, width: '60%', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-elevated)', animation: 'pulse 1.5s ease-in-out infinite' }} />
      </div>
    </div>
  );
}

/**
 * ApprovalDetailPage — 결재 상세 + 단계 타임라인 + 승인/반려 액션
 * 라우트: /approval-center/{requestId}
 * 권한: AUTHENTICATED (상신자/승인자/ADMIN)
 */
export function ApprovalDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const id = requestId ? Number(requestId) : null;

  const requestQuery = useApprovalRequest(id);
  const approveMutation = useApprove(id ?? 0);
  const rejectMutation = useReject(id ?? 0);

  const [dialogMode, setDialogMode] = useState<'approve' | 'reject' | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleApprove = useCallback((comment: string) => {
    if (!id) return;
    approveMutation.mutate(
      { comment },
      {
        onSuccess: (data) => {
          setDialogMode(null);
          setActionError(null);
          if (data.status === 'APPROVED') {
            showToast(t('approval.toast.approved'));
          } else {
            showToast(t('approval.toast.approved'));
          }
        },
        onError: (err) => {
          setDialogMode(null);
          const apiErr = err as unknown as ApiError;
          const errKey = `approval.error.${apiErr.code}`;
          setActionError(t(errKey, { defaultValue: apiErr.message }));
        },
      }
    );
  }, [id, approveMutation, showToast, t]);

  const handleReject = useCallback((comment: string) => {
    if (!id) return;
    rejectMutation.mutate(
      { comment },
      {
        onSuccess: () => {
          setDialogMode(null);
          setActionError(null);
          showToast(t('approval.toast.rejected'));
        },
        onError: (err) => {
          setDialogMode(null);
          const apiErr = err as unknown as ApiError;
          const errKey = `approval.error.${apiErr.code}`;
          setActionError(t(errKey, { defaultValue: apiErr.message }));
        },
      }
    );
  }, [id, rejectMutation, showToast, t]);

  const handleDialogConfirm = useCallback((comment: string) => {
    if (dialogMode === 'approve') handleApprove(comment);
    else if (dialogMode === 'reject') handleReject(comment);
  }, [dialogMode, handleApprove, handleReject]);

  if (!id) {
    return (
      <div style={{ padding: 'var(--spacing-6)' }}>
        <ErrorState
          title={t('approval.error.APPROVAL-404-001')}
          message={t('approval.error.APPROVAL-404-001')}
          onRetry={() => navigate('/approval-center')}
        />
      </div>
    );
  }

  const request = requestQuery.data;
  const isLoading = requestQuery.isLoading;
  const isError = requestQuery.isError;

  const isPendingAction = approveMutation.isPending || rejectMutation.isPending;

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('approval.detail.title')}
        breadcrumb={
          <span>
            <button
              type="button"
              onClick={() => navigate('/approval-center')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-brand-primary)',
                fontSize: 'var(--font-size-sm)',
                padding: 0,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              ← {t('approval.inbox.title')}
            </button>
            {' / '}
            {t('approval.detail.title')}
          </span>
        }
        actions={
          request && (
            <StatusPill tone={statusTone(request.status)}>
              {t(`approval.status.${request.status}`, { defaultValue: request.statusLabel })}
            </StatusPill>
          )
        }
      />

      {/* Loading — skeleton */}
      {isLoading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 'var(--spacing-4)',
          }}
        >
          {/* 메타 카드 skeleton */}
          <div
            style={{
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-4)',
              background: 'var(--color-surface-background)',
            }}
          >
            {[70, 40, 55].map((w, i) => (
              <div
                key={i}
                style={{
                  height: i === 0 ? 18 : 12,
                  width: `${w}%`,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  marginBottom: 'var(--spacing-3)',
                }}
              />
            ))}
          </div>

          {/* 타임라인 skeleton */}
          <div
            style={{
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-4)',
              background: 'var(--color-surface-background)',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-4)', color: 'var(--color-text-secondary)' }}>
              {t('approval.detail.section.timeline')}
            </div>
            {Array.from({ length: 3 }).map((_, i) => <TimelineSkeletonRow key={i} />)}
          </div>
        </div>
      )}

      {/* 오류 */}
      {isError && (
        <div style={{ padding: 'var(--spacing-6)' }}>
          <ErrorState
            title={t('approval.error.APPROVAL-404-001')}
            message={
              (requestQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                ? t('approval.error.AUTH-403-001')
                : (requestQuery.error as unknown as ApiError)?.code === 'APPROVAL-404-001'
                ? t('approval.error.APPROVAL-404-001')
                : ((requestQuery.error as unknown as ApiError)?.message ?? t('approval.error.network'))
            }
            onRetry={() => void requestQuery.refetch()}
          />
        </div>
      )}

      {/* 기본 — 데이터 표시 */}
      {!isLoading && !isError && request && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 'var(--spacing-4)',
          }}
        >
          {/* 결재 메타 정보 */}
          <div
            style={{
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-4)',
              background: 'var(--color-surface-background)',
            }}
          >
            <h2
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-4)',
              }}
            >
              {request.title}
            </h2>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--spacing-3)',
              }}
            >
              {[
                { key: t('approval.detail.field.form'), value: request.formLabel },
                {
                  key: t('approval.detail.field.requester'),
                  value: `${request.requesterName ?? request.requester} (${request.requester})`,
                },
                {
                  key: t('approval.detail.field.submittedAt'),
                  value: formatDateTime(request.submittedAt, i18n.language),
                },
              ].map(({ key, value }) => (
                <div key={key}>
                  <dt
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      fontWeight: 600,
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    {key}
                  </dt>
                  <dd style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
            {request.summary && (
              <p
                style={{
                  marginTop: 'var(--spacing-3)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  background: 'var(--color-surface-muted)',
                  borderRadius: 'var(--radius-sm)',
                  borderLeft: '3px solid var(--color-surface-border)',
                }}
              >
                {request.summary}
              </p>
            )}
          </div>

          {/* 결재 진행 타임라인 */}
          <div
            style={{
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-4)',
              background: 'var(--color-surface-background)',
            }}
          >
            <h3
              style={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-4)',
              }}
            >
              {t('approval.detail.section.timeline')}
            </h3>
            <ApprovalFullTimeline
              steps={request.steps}
              requesterName={request.requesterName}
              submittedAt={request.submittedAt}
            />
          </div>

          {/* 결재 본문 — 양식 동적 필드 */}
          {request.formData && Object.keys(request.formData).length > 0 && (
            <div
              style={{
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--spacing-4)',
                background: 'var(--color-surface-background)',
              }}
            >
              <h3
                style={{
                  fontSize: 'var(--font-size-md)',
                  fontWeight: 700,
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--spacing-4)',
                }}
              >
                {t('approval.detail.section.body')}
              </h3>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 'var(--spacing-3)',
                }}
              >
                {Object.entries(request.formData).map(([key, value]) => (
                  <div key={key}>
                    <dt
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-muted)',
                        fontWeight: 600,
                        marginBottom: 'var(--spacing-1)',
                      }}
                    >
                      {key}
                    </dt>
                    <dd style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                      {typeof value === 'number'
                        ? value.toLocaleString()
                        : String(value ?? '-')}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* 액션 오류 표시 */}
          {actionError && (
            <div
              role="alert"
              aria-live="assertive"
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
                border: '1px solid var(--color-danger)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-danger)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{actionError}</span>
              <button
                type="button"
                onClick={() => setActionError(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-danger)',
                  fontFamily: 'var(--font-family-sans)',
                  fontSize: 'var(--font-size-xs)',
                }}
              >
                ✕
              </button>
            </div>
          )}

          {/* 승인/반려 액션 버튼 — 현 단계 승인자만 (actionable) */}
          {request.actionable && request.status === 'PENDING' && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--spacing-3)',
                padding: 'var(--spacing-4)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-background)',
              }}
            >
              <button
                type="button"
                disabled={isPendingAction}
                onClick={() => setDialogMode('reject')}
                aria-label={t('approval.detail.button.reject')}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-5)',
                  background: isPendingAction ? 'var(--color-surface-muted)' : 'var(--color-danger)',
                  color: isPendingAction ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: isPendingAction ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('approval.detail.button.reject')}
              </button>
              <button
                type="button"
                disabled={isPendingAction}
                onClick={() => setDialogMode('approve')}
                aria-label={t('approval.detail.button.approve')}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-5)',
                  background: isPendingAction ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                  color: isPendingAction ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: isPendingAction ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {isPendingAction ? '...' : t('approval.detail.button.approve')}
              </button>
            </div>
          )}

          {/* 종결 결재 — 읽기 전용 */}
          {request.status !== 'PENDING' && request.decisionComment && (
            <div
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: request.status === 'APPROVED'
                  ? 'color-mix(in srgb, var(--color-success) 8%, transparent)'
                  : 'color-mix(in srgb, var(--color-danger) 8%, transparent)',
                border: `1px solid ${request.status === 'APPROVED' ? 'var(--color-success)' : 'var(--color-danger)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <strong>{t(`approval.status.${request.status}`)}</strong>: {request.decisionComment}
              {request.decidedAt && (
                <span style={{ marginLeft: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  ({formatDateTime(request.decidedAt, i18n.language)})
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* 빈 상태 — requestId 가 null 인 경우는 위에서 처리됨 */}
      {!isLoading && !isError && !request && (
        <div style={{ padding: 'var(--spacing-6)' }}>
          <EmptyState
            title={t('approval.error.APPROVAL-404-001')}
            description={t('approval.error.APPROVAL-404-001')}
            action={
              <button
                type="button"
                onClick={() => navigate('/approval-center')}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                ← {t('approval.inbox.title')}
              </button>
            }
          />
        </div>
      )}

      {/* 승인/반려 다이얼로그 — dialog 상태 */}
      {dialogMode !== null && (
        <ApprovalDecisionDialog
          mode={dialogMode}
          isPending={isPendingAction}
          onConfirm={handleDialogConfirm}
          onCancel={() => setDialogMode(null)}
        />
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: toast.type === 'success'
              ? 'var(--color-success)'
              : 'var(--color-danger)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast.msg}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          dl { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
